/* eslint-disable react-hooks/exhaustive-deps */
import { MedalIcon, UserIconFilled } from '@/components/icons'
import { BracketPaymentLoading } from '@/components/skeleton'
import { LIMIT, OFFSET } from '@/constants/Common'
import { useAppSelector } from '@/hooks/useRedux'
import { useGetBracketStandings } from '@/services'
import { BracketStanding } from '@/types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { eventSelector } from '../selectors'

export const BracketStandings = () => {
  const { t } = useTranslation()

  const { bracket } = useAppSelector(eventSelector)
  const params = useParams()

  const { bracketId } = params

  const [offset, setOffset] = useState(OFFSET)
  const [standings, setStandings] = useState<BracketStanding[]>([])
  const [lastElement, setLastElement] = useState<any>(null)

  const { data: resStandings, isLoading } = useGetBracketStandings(bracketId, LIMIT, offset, 0)

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  useEffect(() => {
    if (resStandings) {
      setStandings([...standings, ...resStandings.result.hits])
    }
  }, [resStandings])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <div className="flex flex-col gap-y-4">
      {standings?.map((item, index) => {
        return index === standings.length - 1 && resStandings?.result.hasMore ? (
          <div key={`${item.registrationId}${index}`} ref={setLastElement} className="flex items-center gap-x-4">
            <div className="w-8 h-8 rounded-full bg-white bg-opacity-70 flex items-center justify-center text-xl text-primary">
              {index + 1}
            </div>
            <div className="flex flex-1 justify-between items-center bg-white rounded-2xl p-4 relative">
              <div className="absolute top-0 left-3">
                {index + 1 <= 3 && bracket?.elimination === 'COMPASS' ? (
                  <MedalIcon
                    className={`w-6 h-6 ${
                      index + 1 === 1 ? 'text-[#fd9a28]' : index + 1 === 2 ? 'text-[#e8e8ed]' : 'text-[#e86769]'
                    }`}
                  />
                ) : null}
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="flex gap-x-2 items-center">
                  {item?.player1?.imageUrl ? (
                    <img src={item?.player1?.imageUrl} className="rounded-full w-7 h-7" alt="" />
                  ) : (
                    <UserIconFilled className="w-7 h-7 text-primary-500" />
                  )}
                  <span className="text-sm font-semibold text-navy-900">{item?.player1?.fullName}</span>
                </div>
                {item.player2 ? (
                  <div className="flex gap-x-2 items-center">
                    {item?.player2?.imageUrl ? (
                      <img src={item?.player2?.imageUrl} className="rounded-full w-7 h-7" alt="" />
                    ) : (
                      <UserIconFilled className="w-7 h-7 text-primary-500" />
                    )}
                    <span className="text-sm font-semibold text-navy-900">{item?.player2?.fullName}</span>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div className="flex items-center gap-x-6">
                <div className="flex flex-col items-center gap-y-2">
                  <span className="text-navy-900 text-xl font-bold">
                    {item?.matchWon}/{item?.matchLost}
                  </span>
                  <span className="text-xs text-gray-500">{t('event.WL')}</span>
                </div>
                <div className="flex flex-col items-center gap-y-2">
                  <span className="text-navy-900 text-xl font-bold">
                    {item?.gameWon}/{item?.gameLost}
                  </span>
                  <span className="text-xs text-gray-500">
                    {t('common.game')} {t('event.WL')}
                  </span>
                </div>
                <div className="flex flex-col items-center gap-y-2">
                  <span className="text-navy-900 text-xl font-bold">{item?.pointDifference}</span>
                  <span className="text-xs text-gray-500">{t('event.pointDiff')}</span>
                </div>
                <div className="flex flex-col items-center gap-y-2">
                  <span className="text-navy-900 text-xl font-bold">
                    {Number(item?.pointDiffPercentage) ? Number(item?.pointDiffPercentage).toFixed(2) : ''}
                  </span>
                  <span className="text-xs text-gray-500">{t('event.pointDiff')} %</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div key={`${item.registrationId}${index}`} className="flex items-center gap-x-4">
            <div className="w-8 h-8 rounded-full bg-white bg-opacity-70 flex items-center justify-center text-xl text-primary">
              {index + 1}
            </div>
            <div className="flex flex-1 justify-between items-center bg-white rounded-2xl p-4 relative">
              <div className="absolute top-0 left-3">
                {index + 1 <= 3 && bracket?.elimination === 'COMPASS' ? (
                  <MedalIcon
                    className={`w-6 h-6 ${
                      index + 1 === 1 ? 'text-[#fd9a28]' : index + 1 === 2 ? 'text-[#e8e8ed]' : 'text-[#e86769]'
                    }`}
                  />
                ) : null}
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="flex gap-x-2 items-center">
                  {item?.player1?.imageUrl ? (
                    <img src={item?.player1?.imageUrl} className="rounded-full w-7 h-7" alt="" />
                  ) : (
                    <UserIconFilled className="w-7 h-7 text-primary-500" />
                  )}
                  <span className="text-sm font-semibold text-navy-900">{item?.player1?.fullName}</span>
                </div>
                {item.player2 ? (
                  <div className="flex gap-x-2 items-center">
                    {item?.player2?.imageUrl ? (
                      <img src={item?.player2?.imageUrl} className="rounded-full w-7 h-7" alt="" />
                    ) : (
                      <UserIconFilled className="w-7 h-7 text-primary-500" />
                    )}
                    <span className="text-sm font-semibold text-navy-900">{item?.player2?.fullName}</span>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div className="flex items-center gap-x-6">
                <div className="flex flex-col items-center gap-y-2">
                  <span className="text-navy-900 text-xl font-bold">
                    {item?.matchWon}/{item?.matchLost}
                  </span>
                  <span className="text-xs text-gray-500">{t('event.WL')}</span>
                </div>
                <div className="flex flex-col items-center gap-y-2">
                  <span className="text-navy-900 text-xl font-bold">
                    {item?.gameWon}/{item?.gameLost}
                  </span>
                  <span className="text-xs text-gray-500">
                    {t('common.game')} {t('event.WL')}
                  </span>
                </div>
                <div className="flex flex-col items-center gap-y-2">
                  <span className="text-navy-900 text-xl font-bold">{item?.pointDifference}</span>
                  <span className="text-xs text-gray-500">{t('event.pointDiff')}</span>
                </div>
                <div className="flex flex-col items-center gap-y-2">
                  <span className="text-navy-900 text-xl font-bold">
                    {Number(item?.pointDiffPercentage) ? Number(item?.pointDiffPercentage).toFixed(2) : ''}
                  </span>
                  <span className="text-xs text-gray-500">{t('event.pointDiff')} %</span>
                </div>
              </div>
            </div>
          </div>
        )
      })}

      {isLoading && (
        <>
          <BracketPaymentLoading />
          <BracketPaymentLoading />
          <BracketPaymentLoading />
          <BracketPaymentLoading />
          <BracketPaymentLoading />
          <BracketPaymentLoading />
        </>
      )}

      {resStandings && !resStandings?.result.hasMore && (
        <div className="flex justify-center">
          <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
        </div>
      )}
    </div>
  )
}
